import { get, post, patch } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
import type {
    CreateSupportTicketMessage,
    SupportTicket,
    SupportTicketMessage,
    SupportTicketStatus,
    SupportTicketTmListItem,
    SupportTicketTmView,
    UpdateSupportTicketStatus,
} from '@policy-tracker/types';

const API_NAME = 'PolicyTracker';

export interface ListResponse<T> {
    items: T[];
    hasMore: boolean;
    nextToken?: string;
}

async function getAuthHeaders(): Promise<Record<string, string>> {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    if (!token) throw new Error('No authentication token available');
    return { Authorization: `Bearer ${token}` };
}

/**
 * TM admin client for the four support ticket endpoints (#2304 part 2).
 *
 *   GET    /api/support-tickets[?tenantId&status]
 *   GET    /api/support-tickets/:tenantId/:ticketId
 *   POST   /api/support-tickets/:tenantId/:ticketId/messages
 *   PATCH  /api/support-tickets/:tenantId/:ticketId  (close|reopen)
 *
 * The TM UI's Amplify REST endpoint `PolicyTracker` is configured to point
 * at `support.api.tenant-management.<domain>` via CloudFront. The browser
 * URLs all begin with `/api/support-tickets/...`; the CloudFront layer
 * strips `/support` so the deployed Lambda receives `/api/support-tickets/...`.
 */
export class SupportService {
    static async listTickets(params?: { tenantId?: string; status?: SupportTicketStatus }): Promise<ListResponse<SupportTicketTmListItem>> {
        const queryParams = new URLSearchParams();
        if (params?.tenantId) queryParams.append('tenantId', params.tenantId);
        if (params?.status) queryParams.append('status', params.status);
        const qs = queryParams.toString();
        const path = qs ? `/api/support-tickets?${qs}` : '/api/support-tickets';
        const headers = await getAuthHeaders();
        const response = await get({ apiName: API_NAME, path, options: { headers } }).response;
        return (await response.body.json()) as unknown as ListResponse<SupportTicketTmListItem>;
    }

    static async getTicket(tenantId: string, ticketId: string): Promise<SupportTicketTmView> {
        const headers = await getAuthHeaders();
        const response = await get({
            apiName: API_NAME,
            path: `/api/support-tickets/${tenantId}/${ticketId}`,
            options: { headers },
        }).response;
        return (await response.body.json()) as unknown as SupportTicketTmView;
    }

    static async addMessage(tenantId: string, ticketId: string, body: CreateSupportTicketMessage): Promise<SupportTicketMessage> {
        const headers = await getAuthHeaders();
        const response = await post({
            apiName: API_NAME,
            path: `/api/support-tickets/${tenantId}/${ticketId}/messages`,
            options: { body, headers },
        }).response;
        return (await response.body.json()) as unknown as SupportTicketMessage;
    }

    static async updateStatus(tenantId: string, ticketId: string, body: UpdateSupportTicketStatus): Promise<SupportTicket> {
        const headers = await getAuthHeaders();
        const response = await patch({
            apiName: API_NAME,
            path: `/api/support-tickets/${tenantId}/${ticketId}`,
            options: { body, headers },
        }).response;
        return (await response.body.json()) as unknown as SupportTicket;
    }
}

export default SupportService;
